import { FC } from "react";

import { PillBannerButton } from "components/footer/PillBannerButton";
import { type PresentationBannerColorType } from "components/footer/PresentationBanner";
import { t } from "i18n/i18n";
import { useSectionCapabilitiesBySectionId } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectActiveClassSession,
  selectCurrentSectionId,
  selectCurrentUserId,
  selectIsPresentationActive,
  selectPresentedPageId,
} from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { useJoinAsSectionAssistant } from "utils/hooks/use-join-as-section-assistant";
import { setPresentationWindow, takeOverPresenting } from "utils/presentation";
import { Link } from "utils/routing";
import { pageUrl } from "utils/urls";

interface TakeOverPresentationButtonProps {
  color: PresentationBannerColorType;
}

export const TakeOverPresentationButton: FC<TakeOverPresentationButtonProps> = ({ color }) => {
  const userId = useAppSelector(selectCurrentUserId);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const classSession = useObjectSelector(selectActiveClassSession, ["id", "presenting_user_id"]);
  const isPresentationActive = useAppSelector(selectIsPresentationActive);
  const presentedPageId = useAppSelector(selectPresentedPageId);
  const isPresentingUser = classSession?.presenting_user_id === userId;
  const { joinAsSectionAssistantIfNew } = useJoinAsSectionAssistant();
  const { canAuthorSection } = useSectionCapabilitiesBySectionId(sectionId);

  if (
    !sectionId ||
    !isPresentationActive ||
    !presentedPageId ||
    !canAuthorSection ||
    isPresentingUser
  ) {
    return null;
  }

  const presentedPageUrl = pageUrl(sectionId, presentedPageId);

  const onTakeOverPresentation = async () => {
    await joinAsSectionAssistantIfNew();

    setPresentationWindow();
    if (classSession?.id) {
      return takeOverPresenting(classSession.id, userId ?? "");
    }
  };

  return (
    <PillBannerButton
      as={Link}
      eventName="Presentation - Take over presentation"
      kind={`banner-${color}`}
      to={presentedPageUrl}
      onClick={onTakeOverPresentation}
    >
      {t("main_header.take_over_presentation")}
    </PillBannerButton>
  );
};
